import React, { useCallback, useMemo, useState } from "react";
import AsyncActionButton from "./AsyncActionButton";
import { useLanguage } from "../i18n/LanguageContext";
import { detectReceiptCategory, getReceiptCategoryOptions, normalizeReceiptCategory } from "../services/receiptCategoryService";
import "../styles/scanModal.css";

function formatFiyat(val) {
  const n = parseFloat(String(val).replace(",", "."));
  return isNaN(n) ? "0.00" : n.toFixed(2);
}

/**
 * Market fişi tarama sonuçlarını gösteren modal.
 * Kullanıcı ürün adları, kategorileri ve fiyatlarını düzenleyip harcamalara ekler.
 */
export default function ReceiptPreviewModal({
  items = [],
  categories = [],
  categoryLabel = (value) => value,
  onConfirm,
  onClose,
}) {
  const { t, lang } = useLanguage();

  const kategoriSecenekleri = useMemo(() => getReceiptCategoryOptions(categories), [categories]);

  const [secilen, setSecilen] = useState(() =>
    Object.fromEntries(items.map((_, i) => [i, true]))
  );
  const [duzenlenen, setDuzenlenen] = useState(() =>
    items.map((item) => {
      const ad = String(item?.ad || item?.name || "").trim();
      const rawKategori = item?.kategori || item?.category || item?.expenseCategory || item?.harcamaKategori;
      return {
        ad,
        fiyat: formatFiyat(item?.fiyat ?? item?.price ?? 0),
        kategori: rawKategori ? normalizeReceiptCategory(rawKategori, categories) : detectReceiptCategory(ad, categories),
      };
    })
  );

  const seciliSayisi = useMemo(
    () => Object.values(secilen).filter(Boolean).length,
    [secilen]
  );

  const toplamTutar = useMemo(() => {
    return Object.entries(secilen)
      .filter(([, v]) => v)
      .reduce((sum, [i]) => {
        const f = parseFloat(String(duzenlenen[i]?.fiyat || "0").replace(",", "."));
        return sum + (isNaN(f) ? 0 : f);
      }, 0);
  }, [secilen, duzenlenen]);

  const hepsiSecili = useMemo(() => {
    return duzenlenen.length > 0 && duzenlenen.every((_, idx) => secilen[idx]);
  }, [duzenlenen, secilen]);

  const handleTumunuSec = useCallback(() => {
    setSecilen(Object.fromEntries(duzenlenen.map((_, i) => [i, true])));
  }, [duzenlenen]);

  const handleSecimiKaldir = useCallback(() => {
    setSecilen(Object.fromEntries(duzenlenen.map((_, i) => [i, false])));
  }, [duzenlenen]);

  const handleToggle = useCallback((idx) => {
    setSecilen((prev) => ({ ...prev, [idx]: !prev[idx] }));
  }, []);

  const handleEditAd = useCallback((idx, val) => {
    setDuzenlenen((prev) => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        ad: val,
        kategori: detectReceiptCategory(val, categories),
      };
      return next;
    });
  }, [categories]);

  const handleEditFiyat = useCallback((idx, val) => {
    setDuzenlenen((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], fiyat: val };
      return next;
    });
  }, []);

  const handleEditKategori = useCallback((idx, val) => {
    setDuzenlenen((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], kategori: normalizeReceiptCategory(val, categories) };
      return next;
    });
  }, [categories]);

  const handleConfirm = async () => {
    const sonuc = duzenlenen
      .map((item, i) => ({ ...item, secili: secilen[i] }))
      .filter((x) => x.secili && x.ad?.trim().length > 0)
      .map((x) => {
        const kategori = normalizeReceiptCategory(x.kategori || detectReceiptCategory(x.ad, categories), categories);
        return {
          ad: x.ad.trim(),
          fiyat: parseFloat(String(x.fiyat).replace(",", ".")) || 0,
          kategori,
          category: kategori,
          harcamaKategori: kategori,
          expenseCategory: kategori,
        };
      })
      .filter((x) => x.fiyat > 0);

    if (sonuc.length > 0) return onConfirm(sonuc);
    return undefined;
  };

  return (
    <div className="scanModalOverlay" onClick={onClose}>
      <div className="scanModalCard receiptCard" onClick={(e) => e.stopPropagation()}>
        <div className="scanModalHeader">
          <div className="scanModalTitle">
            <span className="scanModalEmoji">🧾</span>
            <div>
              <strong>{t("scan.previewTitleReceipt")}</strong>
              <p>{items.length} {lang === "en" ? "items on receipt" : "fiş kalemi tespit edildi"}</p>
            </div>
          </div>
          <button className="scanModalClose" onClick={onClose}>✕</button>
        </div>

        <div className="scanBulkRow">
          <button className="scanBulkBtn" type="button" onClick={handleTumunuSec} disabled={hepsiSecili}>
            {lang === "en" ? "Select all" : "Tümünü seç"}
          </button>
          <button className="scanBulkBtn" type="button" onClick={handleSecimiKaldir} disabled={seciliSayisi === 0}>
            {lang === "en" ? "Clear selection" : "Seçimi kaldır"}
          </button>
        </div>

        <div className="receiptTableHead receiptTableHeadV2">
          <span className="rColCheck" />
          <span className="rColMain">{lang === "en" ? "Item / Category" : "Ürün / Kategori"}</span>
          <span className="rColPrice">{lang === "en" ? "Price" : "Fiyat"}</span>
        </div>

        <div className="scanItemList receiptItemList">
          {duzenlenen.map((item, idx) => (
            <div
              key={idx}
              className={`scanItem receiptItem receiptItemV2${secilen[idx] ? " selected" : ""}`}
            >
              <span
                className={`scanCheckbox${secilen[idx] ? " checked" : ""}`}
                onClick={() => handleToggle(idx)}
              >
                {secilen[idx] ? "✓" : ""}
              </span>

              <div className="receiptProductCell">
                <input
                  className="scanItemInput receiptNameInput"
                  value={item.ad}
                  onChange={(e) => handleEditAd(idx, e.target.value)}
                  placeholder={lang === "en" ? "Item name" : "Ürün adı"}
                />
                <select
                  className="receiptCategorySelect"
                  value={item.kategori}
                  onChange={(e) => handleEditKategori(idx, e.target.value)}
                  aria-label={lang === "en" ? "Category" : "Kategori"}
                >
                  {kategoriSecenekleri.map((k) => (
                    <option key={k} value={k}>{categoryLabel(k)}</option>
                  ))}
                </select>
              </div>

              <input
                className="scanItemInput rColPrice receiptPriceInput"
                value={item.fiyat}
                onChange={(e) => handleEditFiyat(idx, e.target.value)}
                inputMode="decimal"
                placeholder="0,00"
              />
            </div>
          ))}
        </div>

        <div className="receiptTotal">
          <span>{lang === "en" ? "Selected Total" : "Seçili Toplam"}</span>
          <strong>{toplamTutar.toLocaleString(lang === "en" ? "en-US" : "tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺</strong>
        </div>

        <div className="scanModalFooter">
          <span className="scanSelectedCount">
            {seciliSayisi} {lang === "en" ? "item selected" : "kalem seçildi"}
          </span>
          <div className="scanFooterBtns">
            <button className="darkSecondaryBtn" onClick={onClose}>
              {lang === "en" ? "Cancel" : "İptal"}
            </button>
            <AsyncActionButton
              className="primaryButton"
              onAction={handleConfirm}
              disabled={seciliSayisi === 0}
            >
              {lang === "en" ? "Add to Expenses" : "Harcamalara Ekle"}
            </AsyncActionButton>
          </div>
        </div>
      </div>
    </div>
  );
}
